import { useCallback, useEffect, useState } from 'react';
import { LeadDrawer } from '../components/LeadDrawer';
import { mailApi, type InboundEmail } from '../lib/mailApi';

const FILTERS = [
  { key: 'todo', label: 'Replies to answer', params: { kind: 'reply', handled: false } },
  { key: 'replies', label: 'All replies', params: { kind: 'reply' } },
  { key: 'auto_reply', label: 'Auto-replies', params: { kind: 'auto_reply' } },
  { key: 'bounce', label: 'Bounces', params: { kind: 'bounce' } },
  { key: 'unsubscribe', label: 'Unsubscribes', params: { kind: 'unsubscribe' } },
  { key: 'all', label: 'Everything', params: {} },
] as const;

const KIND_LABEL: Record<InboundEmail['kind'], string> = {
  reply: 'Reply',
  auto_reply: 'Auto-reply',
  bounce: 'Bounce',
  unsubscribe: 'Unsubscribe',
};

export function InboxPage({ onCountChange }: { onCountChange?: (n: number) => void }) {
  const [filter, setFilter] = useState<(typeof FILTERS)[number]['key']>('todo');
  const [items, setItems] = useState<InboundEmail[]>([]);
  const [openLead, setOpenLead] = useState<string | null>(null);

  const load = useCallback(() => {
    const f = FILTERS.find((x) => x.key === filter)!;
    void mailApi
      .inbox(f.params as { kind?: string; handled?: boolean })
      .then((r) => {
        setItems(r.items);
        onCountChange?.(r.unhandledReplies);
      })
      .catch(() => undefined);
  }, [filter, onCountChange]);
  useEffect(load, [load]);

  return (
    <div className="mx-auto max-w-5xl space-y-6 px-6 py-8">
      <div>
        <h2 className="text-lg font-bold text-ink">Inbox</h2>
        <p className="text-sm text-slate-500">
          Replies to your campaigns, picked up from your mailbox every 3 minutes. Answer them from your normal email; they're in the same thread. Other mail in your mailbox is never stored here.
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)} className={`rounded-full border px-3 py-1 text-sm font-semibold ${filter === f.key ? 'border-brand bg-brand text-white' : 'border-line bg-white text-slate-600 hover:text-ink'}`}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {items.map((m) => (
          <div key={m.id} className={`card space-y-2 ${m.handled ? 'opacity-60' : ''}`}>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${m.kind === 'reply' ? 'bg-green-100 text-green-700' : m.kind === 'bounce' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}`}>{KIND_LABEL[m.kind]}</span>
              <span className="font-semibold text-ink">{m.fromName ? `${m.fromName} <${m.fromEmail}>` : m.fromEmail}</span>
              {m.lead && (
                <button className="text-brand-dark hover:underline" onClick={() => setOpenLead(m.lead!.id)}>{m.lead.businessName}</button>
              )}
              {m.campaignLead && <span className="text-xs text-slate-400">· {m.campaignLead.campaign.name}</span>}
              <span className="ml-auto text-xs text-slate-400">{new Date(m.receivedAt).toLocaleString()}</span>
            </div>
            {m.subject && <div className="text-sm font-semibold text-ink">{m.subject}</div>}
            <pre className="max-h-48 overflow-auto whitespace-pre-wrap font-sans text-sm text-slate-700">{m.snippet}</pre>
            <button className="text-xs font-semibold text-slate-500 hover:text-ink" onClick={() => void mailApi.setHandled(m.id, !m.handled).then(load)}>
              {m.handled ? 'Mark as not done' : 'Mark as done'}
            </button>
          </div>
        ))}
        {!items.length && <div className="card text-sm text-slate-400">Nothing here.</div>}
      </div>

      {openLead && <LeadDrawer leadId={openLead} onClose={() => setOpenLead(null)} onChanged={load} />}
    </div>
  );
}
